// React & Core
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Loader from '@/shared/components/ui/Loader/Loader';
import { useLoadingStore } from '@/store/loadingStore';

// Styles
import '@/styles/index.css';

// i18n
import '@/shared/utils/i18n.js';

import { ToastContainer } from 'react-toastify';
// asset
import AmbulanceManagement from '@/domains/asset-management/pages/AmbulanceManagement/AmbulanceManagement';
// Asset Management
import BoxManagement from '@/domains/asset-management/pages/BoxManagement/BoxManagement';
import CameraManagement from '@/domains/asset-management/pages/CameraManagement/CameraManagement';
import DetectorManagement from '@/domains/asset-management/pages/DetectorManagement/DetectorManagement';
import EnvironmentSensor from '@/domains/asset-management/pages/EnvironmentSensor/EnvironmentSensor';
import FacilityManagement from '@/domains/asset-management/pages/FacilityManagement/FacilityManagement';
// smartcityDashboard
import FacilityManagementCopy from '@/domains/asset-management/pages/FacilityManagement/FacilityManagementCopy';
// asset
import HealthCareManagement from '@/domains/asset-management/pages/HealthCareManagement/HealthCareManagement';
import InfraManagement from '@/domains/asset-management/pages/InfraManagement/InfraManagement';
import LogoJectorManagement from '@/domains/asset-management/pages/LogoJectorManagement/LogoJectorManagement';
// Auth
import SignIn from '@/domains/auth/pages/SignIn/SignIn';
import EnvSensorCollectionData from '@/domains/log/pages/EnvSensorCollectionData/EnvSensorCollectionData';
import HealthcareReportManagement from '@/domains/report-management/pages/HealthcareReportManagement/HealthcareReportManagement';
import ReportManagement from '@/domains/report-management/pages/ReportManagement/ReportManagement';
import ScenarioDeviceManagementPage from '@/domains/scenario/pages/DeviceManagement/DeviceManagement';
// Scenario
import EventManagementPage from '@/domains/scenario/pages/EventManagement/EventManagement';
import ScenarioManagement from '@/domains/scenario/pages/ScenarioManagement/ScenarioManagement';
// Site Management
import SiteManagement from '@/domains/site-management/pages/SiteManagement.js';
// Log
import CommunicationHistory from '@/domains/statistic/pages/CommunicationHistory/CommunicationHistory';
// Statistics
import CommunicationStatistic from '@/domains/statistic/pages/CommunicationStatistic/CommunicationStatistic';
import DirectionalTrafficStatisticPage from '@/domains/statistic/pages/DirectionalTrafficStatistic/DirectionalTrafficStatisticPage';
import EnvironmentStatistic from '@/domains/statistic/pages/EnvironmentStatistic/EnvironmentStatistic';
import SuddenEventManagementPage from '@/domains/statistic/pages/SuddenEventManagement/SuddenEventManagementPage';
import AuthorityManagement from '@/domains/system-management/pages/AuthorityManagement/AuthorityManagement';
import CodeManagement from '@/domains/system-management/pages/CodeManagement/CodeManagement';
import GroupManagement from '@/domains/system-management/pages/GroupManagement/GroupManagement';
import PreferencesManagement from '@/domains/system-management/pages/PreferenceManagement/PreferenceManagement';
import ProgramManagement from '@/domains/system-management/pages/ProgramManagement/ProgramManagement';
// System Management
import Unauthorized from '@/domains/system-management/pages/Unauthorized/Unauthorized';
import UserManagement from '@/domains/system-management/pages/UserManagement/UserManagement';
// Dashboard
// import AccessRoadPedPage from '@/domains/traffic-monitoring/pages/AccessRoadPed/AccessRoadPedPage';
import AmbulanceDispatch from '@/domains/traffic-monitoring/pages/AmbulanceDispatch/AmbulanceDispatch';
import CCTVDashboard from '@/domains/traffic-monitoring/pages/CCTVDashboard/CCTVDashboard';
import CCTVDetailDashboard from '@/domains/traffic-monitoring/pages/CCTVDetailDashboard/CCTVDetailDashboard';
// import CrossRoadPed from '@/domains/traffic-monitoring/pages/CrossRoadPed/CrossRoadPed';
// import EnvDashboard from '@/domains/traffic-monitoring/pages/EnvDashboard/EnvDashboard';
// import MainDashboard from '@/domains/traffic-monitoring/pages/MainDashboard/MainDashboard';
import NotificationDashboard from '@/domains/traffic-monitoring/pages/NotificationDashboard/NotificationDashboard';
import PSCDashboard from '@/domains/traffic-monitoring/pages/PSCDashboard/PSCDashboard';
import ReportDetails from '@/domains/traffic-monitoring/pages/ReportDetails/ReportDetails';
import ReportRegistration from '@/domains/traffic-monitoring/pages/ReportRegistration/ReportRegistration';
// smartcityDashboard
import SmartCityDashboard from '@/domains/traffic-monitoring/pages/SmartCityDashboard/SmartCityDashboard';
// import TotalDashboard from '@/domains/traffic-monitoring/pages/TotalDashboard/TotalDashboardPage';
import TrafficDashboard from '@/domains/traffic-monitoring/pages/TrafficDashboard/TrafficDashboard';
import DevErrorOverlay from '@/shared/components/DevErrorOverlay';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import ProtectedRoutes from '@/shared/components/layout/ProtectedRoutes/ProtectedRoutes';
// Shared
import VideoModal from '@/shared/components/ui/Modal/VideoModal/VideoModal.jsx';
import SessionWatcher from '@/shared/components/ui/SessionWatcher/SessionWatcher';
import { ensureMapProviderResolved } from '@/shared/config/map';
import { PreferenceProvider } from '@/shared/context/preferenceContext.jsx';
import 'react-toastify/dist/ReactToastify.css';
import Introduction from '@/domains/landing-page/pages/Introduction/Introduction';
import LocationDetails from '@/domains/landing-page/pages/LocationDetails/LocationDetails';
import MonitoringStatus from '@/domains/landing-page/pages/MonitoringStatus/MonitoringStatus';
import NotFound from '@/domains/landing-page/pages/NotFound/NotFound';
import LanguageManagement from '@/domains/system-management/pages/LanguageManagement/LanguageManagement';
import EventDetailsDashboard from '@/domains/traffic-monitoring/pages/EventDetailsDashboard/EventDetailsDashboard';
import { keepTsdbNavigation, tsdbRoute } from '@/domains/tsdb-evaluation/tsdbRouting';

// smartcityDashboard
const queryClient = new QueryClient();

const router = createBrowserRouter([
  // Public Routes
  { path: '/', element: <SignIn /> },
  { path: '/img-modal', element: <VideoModal /> },
  { path: 'introduction', element: <Introduction /> },

  {
    path: 'landing',
    children: [
      { path: 'location-detail/:token', element: <LocationDetails /> },
      { path: 'monitoring-status/:reportCode', element: <MonitoringStatus /> },
    ],
  },

  // Protected Routes
  {
    element: <ProtectedRoutes />,
    children: [
      // Management
      {
        path: 'management',
        children: [{ path: 'unauthorized', element: <Unauthorized /> }],
      },

      // System Management
      {
        path: 'system-management',
        children: [
          { path: 'user', element: <UserManagement /> },
          { path: 'program', element: <ProgramManagement /> },
          { path: 'code', element: <CodeManagement /> },
          { path: 'authority', element: <AuthorityManagement /> },
          { path: 'group', element: <GroupManagement /> },
          { path: 'preference', element: <PreferencesManagement /> },
          { path: 'language', element: <LanguageManagement /> },
        ],
      },

      // Asset Management
      {
        // path: 'facility-management',
        path: 'asset-management',
        children: [
          { path: 'box', element: <BoxManagement /> },
          { path: 'speaker', element: <FacilityManagementCopy /> },
          // {
          //   path: 'facility',
          //   element: <FacilityManagement />,
          // },
          { path: 'logojector', element: <LogoJectorManagement /> },
          { path: 'ambulance', element: <AmbulanceManagement /> },
          { path: 'camera', element: <CameraManagement /> },
          { path: 'infra', element: <InfraManagement /> },
          { path: 'facility', element: <FacilityManagement /> },
          { path: 'healthcare', element: <HealthCareManagement /> },
        ],
      },
      {
        // path: 'facility-management',
        path: 'asset-management',
        children: [
          { path: 'box', element: <BoxManagement /> },
          { path: 'speaker', element: <FacilityManagementCopy /> },
          // {
          //   path: 'facility',
          //   element: <FacilityManagement />,
          // },
          { path: 'logojector', element: <LogoJectorManagement /> },
          { path: 'ambulance', element: <AmbulanceManagement /> },
          { path: 'camera', element: <CameraManagement /> },
          { path: 'infra', element: <InfraManagement /> },
          { path: 'facility', element: <FacilityManagement /> },
          { path: 'healthcare', element: <HealthCareManagement /> },
        ],
      },

      // report management
      {
        path: 'report-management',
        children: [
          { path: 'report', element: <ReportManagement /> },
          { path: 'healthcare-report', element: <HealthcareReportManagement /> },
        ],
      },

      // Site Management
      {
        path: 'site-management',
        children: [{ path: 'site', element: <SiteManagement /> }],
      },

      // Statistics
      {
        path: 'statistic',
        children: [
          {
            path: 'communication-statistic',
            element: <CommunicationStatistic />,
          },
          {
            path: 'directional-traffic',
            element: <DirectionalTrafficStatisticPage />,
          },
          { path: 'sudden-event', element: <SuddenEventManagementPage /> },
          { path: 'environment-statistic', element: <EnvironmentStatistic /> },
        ],
      },

      // Log
      {
        path: 'log',
        children: [
          { path: 'sensor-data', element: <CommunicationHistory /> },
          { path: 'env-collection', element: <EnvSensorCollectionData /> },
        ],
      },

      // Scenario
      {
        path: 'scenario',
        children: [
          { path: 'event', element: <EventManagementPage /> },
          { path: 'device', element: <ScenarioDeviceManagementPage /> },
          { path: 'scenario-management', element: <ScenarioManagement /> },
        ],
      },

      {
        path: 'sensor-management',
        children: [
          { path: 'detector-sensor', element: <DetectorManagement /> },
          { path: 'environment-sensor', element: <EnvironmentSensor /> },
        ],
      },

      // Dashboard
      {
        path: 'dashboard',
        children: [
          // { path: 'main-dashboard', element: <MainDashboard /> },
          // { path: 'env-dashboard/:sensorId', element: <EnvDashboard /> },
          // { path: 'total-dashboard', element: <TotalDashboard /> },
          // { path: 'crossroad-dashboard/:siteId', element: <CrossRoadPed /> },
          // {
          //   path: 'accessroad-dashboard/:siteId/:roadId',
          //   element: <AccessRoadPedPage />,
          // },
          { path: 'smartCity-dashboard', element: <SmartCityDashboard /> },
          { path: 'psc-dashboard', element: <PSCDashboard /> },
          { path: 'regis-dashboard', element: <ReportRegistration /> },
          { path: 'ambulance-dashboard', element: <AmbulanceDispatch /> },
          { path: 'details-dashboard', element: <ReportDetails /> },
          { path: 'traffic-dashboard', element: <TrafficDashboard /> },
          { path: 'cctv-dashboard', element: <CCTVDashboard /> },
          { path: 'cctvDetail-dashboard', element: <CCTVDetailDashboard /> },
          { path: 'notif-dashboard', element: <NotificationDashboard /> },
          { path: 'event-dashboard', element: <EventDetailsDashboard /> },
          // TimescaleDB evaluation copies at /dashboard/tsdb/<page> (src/domains/tsdb-evaluation)
          tsdbRoute({
            'smartCity-dashboard': <SmartCityDashboard />,
            'traffic-dashboard': <TrafficDashboard />,
            'event-dashboard': <EventDetailsDashboard />,
            'cctv-dashboard': <CCTVDashboard />,
            'cctvDetail-dashboard': <CCTVDetailDashboard />,
            'psc-dashboard': <PSCDashboard />,
            'ambulance-dashboard': <AmbulanceDispatch />,
          }),
        ],
      },
    ],
  },

  { path: '*', element: <NotFound /> },
]);

keepTsdbNavigation(router);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Kick off the map-provider probe at boot (non-blocking) so it is ready by the
// time the user reaches a map page; rendering does not wait for it. Map
// components gate their own mount on this, so a slow probe never delays the app.
ensureMapProviderResolved();

// React.StrictMode intentionally disabled: the OpenLayers map components
// (MainDashboardMaps / SmartCityMaps) init the map in useEffect and are NOT
// StrictMode-safe. In dev, StrictMode double-mounts (mount -> cleanup -> mount)
// and re-inits OpenLayers on the same DOM node, which throws
// "Failed to execute 'insertBefore' on 'Node'" during React commit.
// This is a dev-only artifact (production builds do not double-mount).
// Re-enable once the map effects are made idempotent (guard double-init).
// Loader fullscreen saat kamus translasi sedang dimuat (bootstrap /
// ganti bahasa) - state-nya diisi dari ProtectedRoutes, useAuth, SwitchButton.
const TranslationLoader = () => {
  const translationLoading = useLoadingStore((state) => state.translationLoading);
  return <Loader isOpen={translationLoading} />;
};

ReactDOM.createRoot(rootElement).render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <PreferenceProvider>
        <RouterProvider router={router} />
        <TranslationLoader />
        <SessionWatcher />
        <ToastContainer
          position="top-right"
          autoClose={1500}
          hideProgressBar
          closeButton={false}
          toastStyle={{
            width: '500px',
            padding: 0,
            background: 'transparent',
            boxShadow: 'shadow-lg',
          }}
        />
      </PreferenceProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
    <DevErrorOverlay />
  </ErrorBoundary>
);
